import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { DollarSign, Users, Calculator, ShieldAlert, Gauge, BarChart3, X, MessageSquare, Mail, KeyRound, Copy, FileWarning } from 'lucide-react';

import useAuthStore from '../stores/authStore';
import { BackendAPI } from '../lib/api';
import { cn } from '../lib/utils';
import AnalysisDashboardPage from './AnalysisDashboardPage';
import LifecycleTab from './admin/LifecycleTab';
import FailuresTab from './admin/FailuresTab';
import FeatureGrantsPanel from '../components/admin/FeatureGrantsPanel';
import Sparkline from '../components/admin/Sparkline';
import { AccessCells, AccessHeaderCells, ACCESS_COLUMN_COUNT } from './admin/UserAccessColumns';
import { AdminUserAccessAPI, type UserAccessSummary } from '../lib/adminUserAccessApi';

// =========================================================================
// Admin Console
// =========================================================================
// Three tabs:
//   Cost      — daily stacked chart + hourly (today) + per-endpoint costs
//   Users     — roster with project counts, spend, role checkboxes
//   Pricing   — "if a user runs N estimates/month, expected cost is $X"
//
// Gated client-side by useAuthStore().roles. Server enforces require_role
// on every /admin/* endpoint so direct API hits also 403.
// =========================================================================

type TabId =
  | 'cost'
  | 'users'
  | 'feedback'
  | 'lifecycle'
  | 'pricing'
  | 'thresholds'
  | 'analysis'
  | 'failures'
  | 'partners';

const TAB_IDS: TabId[] = [
  'cost', 'users', 'feedback', 'lifecycle', 'pricing', 'thresholds', 'analysis', 'failures', 'partners',
];

export default function AdminPage() {
  const { roles } = useAuthStore();
  const isAdmin = roles.includes('admin') || roles.includes('super_admin');
  const isSuperAdmin = roles.includes('super_admin');
  // Deep links from the failed-import alert email: ?tab=failures&id=<failure id>.
  const [params] = useSearchParams();
  const linkedTab = params.get('tab') as TabId | null;
  const [tab, setTab] = useState<TabId>(
    linkedTab && TAB_IDS.includes(linkedTab) ? linkedTab : 'cost',
  );

  if (!isAdmin) {
    // Defense in depth — the sidebar already hides the link, the API
    // already 403s. This is the third layer.
    return (
      <div className="mx-auto max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500 dark:text-amber-400" />
        <h1 className="mt-3 text-lg font-semibold">Admin access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You do not have permission to view this page. If you think this is
          a mistake, ask a super admin to grant you the <code>admin</code> role.
        </p>
        <Navigate to="/" replace />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admin Console</h1>
        <p className="text-sm text-muted-foreground">
          Cost monitoring, user management, and pricing analysis.
        </p>
      </div>

      <div className="flex flex-wrap gap-x-2 border-b">
        <TabButton active={tab === 'cost'} onClick={() => setTab('cost')} icon={DollarSign} label="Cost" />
        <TabButton active={tab === 'users'} onClick={() => setTab('users')} icon={Users} label="Users" />
        <TabButton active={tab === 'feedback'} onClick={() => setTab('feedback')} icon={MessageSquare} label="Feedback" />
        <TabButton active={tab === 'lifecycle'} onClick={() => setTab('lifecycle')} icon={Mail} label="Lifecycle" />
        <TabButton active={tab === 'thresholds'} onClick={() => setTab('thresholds')} icon={Gauge} label="Thresholds" />
        <TabButton active={tab === 'pricing'} onClick={() => setTab('pricing')} icon={Calculator} label="Pricing" />
        <TabButton active={tab === 'analysis'} onClick={() => setTab('analysis')} icon={BarChart3} label="Analysis" />
        {isSuperAdmin && (
          <TabButton active={tab === 'failures'} onClick={() => setTab('failures')} icon={FileWarning} label="Failures" />
        )}
        {isSuperAdmin && (
          <TabButton active={tab === 'partners'} onClick={() => setTab('partners')} icon={KeyRound} label="Partners" />
        )}
      </div>

      {tab === 'cost' && <CostTab />}
      {tab === 'users' && <UsersTab isSuperAdmin={isSuperAdmin} />}
      {tab === 'feedback' && <FeedbackTab />}
      {tab === 'lifecycle' && <LifecycleTab />}
      {tab === 'thresholds' && <ThresholdsTab />}
      {tab === 'pricing' && <PricingTab />}
      {tab === 'analysis' && <AnalysisDashboardPage />}
      {tab === 'failures' && isSuperAdmin && <FailuresTab initialId={params.get('id')} initialSeverity={params.get('severity') ?? ''} />}
      {tab === 'partners' && isSuperAdmin && <PartnersTab />}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab switcher
// -------------------------------------------------------------------------

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon: Icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
      active
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    )}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

// =========================================================================
// COST TAB
// =========================================================================

interface DailyResp {
  days: Array<{
    date: string;
    total_usd: number;
    by_model: Record<string, number>;
    by_provider: Record<string, number>;
  }>;
  total_usd: number;
}

interface HourlyResp {
  date: string;
  hours: Array<{ hour: number; total_usd: number; by_model: Record<string, number> }>;
}

interface ByEndpointResp {
  endpoints: Array<{
    endpoint: string;
    request_count: number;
    total_usd: number;
    avg_usd: number;
    p95_usd: number;
  }>;
}

const CostTab: React.FC = () => {
  const [daily, setDaily] = useState<DailyResp | null>(null);
  const [hourly, setHourly] = useState<HourlyResp | null>(null);
  const [endpoints, setEndpoints] = useState<ByEndpointResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [userId, setUserId] = useState('');
  const [userOptions, setUserOptions] = useState<Array<{ id: string; label: string }>>([]);

  // User-picker options (load once). Lets the founder see one user's API cost.
  useEffect(() => {
    BackendAPI.adminListUsers({ days: 30 })
      .then((r) =>
        setUserOptions(
          r.users
            .filter((u) => u.email)
            .map((u) => ({ id: u.id, label: u.name ? `${u.name} (${u.email})` : u.email })),
        ),
      )
      .catch(() => setUserOptions([]));
  }, []);

  useEffect(() => {
    setError(null);
    const uid = userId || undefined;
    Promise.all([
      BackendAPI.adminUsageDaily({ days, user_id: uid }),
      BackendAPI.adminUsageHourly({ user_id: uid }),
      BackendAPI.adminUsageByEndpoint({ days, user_id: uid }),
    ])
      .then(([d, h, e]) => {
        setDaily(d);
        setHourly(h);
        setEndpoints(e);
      })
      .catch((err) => setError(err?.message || 'Failed to load usage data'));
  }, [days, userId]);

  const todayTotal = useMemo(() => {
    if (!hourly) return 0;
    return hourly.hours.reduce((sum, h) => sum + h.total_usd, 0);
  }, [hourly]);

  if (error) {
    return <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-4 text-sm text-red-800 dark:text-red-300">{error}</div>;
  }
  if (!daily || !hourly || !endpoints) {
    return <div className="text-sm text-muted-foreground">Loading cost data...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium text-muted-foreground">Filter by user</label>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="rounded border px-2 py-1 text-xs"
          aria-label="Filter cost by user"
        >
          <option value="">All users</option>
          {userOptions.map((u) => (
            <option key={u.id} value={u.id}>{u.label}</option>
          ))}
        </select>
        {userId && (
          <button
            type="button"
            onClick={() => setUserId('')}
            className="text-xs text-primary hover:underline"
          >
            Clear
          </button>
        )}
        {userId && (
          <span className="text-xs text-muted-foreground">
            Showing one user’s attributed API cost.
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard label="Today" value={formatUsd(todayTotal)} />
        <SummaryCard label={`Last ${days} days`} value={formatUsd(daily.total_usd)} />
        <SummaryCard
          label="Avg / day"
          value={formatUsd(daily.total_usd / Math.max(1, daily.days.length))}
        />
      </div>

      <Panel
        title={`Daily spend, last ${days} days`}
        right={
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="rounded border px-2 py-1 text-xs"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        }
      >
        <StackedBarChart
          buckets={daily.days.map((d) => ({ label: d.date.slice(5), segments: d.by_model, total: d.total_usd }))}
          ariaLabel="Daily LLM cost stacked by model"
        />
      </Panel>

      <Panel title={`Hourly spend, today (${hourly.date} UTC)`}>
        <StackedBarChart
          buckets={hourly.hours.map((h) => ({
            label: `${String(h.hour).padStart(2, '0')}h`,
            segments: h.by_model,
            total: h.total_usd,
          }))}
          ariaLabel="Hourly LLM cost stacked by model"
        />
      </Panel>

      <Panel title="Per-endpoint cost (per request, fan-out aware)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">Endpoint</th>
                <th className="pb-2 text-right">Requests</th>
                <th className="pb-2 text-right">Avg / request</th>
                <th className="pb-2 text-right">P95 / request</th>
                <th className="pb-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.endpoints.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-3 text-center text-muted-foreground">
                    No LLM calls in this window.
                  </td>
                </tr>
              )}
              {endpoints.endpoints.map((row) => (
                <tr key={row.endpoint} className="border-t">
                  <td className="py-2 font-mono text-xs">{row.endpoint}</td>
                  <td className="py-2 text-right">{row.request_count}</td>
                  <td className="py-2 text-right">{formatUsd(row.avg_usd)}</td>
                  <td className="py-2 text-right">{formatUsd(row.p95_usd)}</td>
                  <td className="py-2 text-right font-medium">{formatUsd(row.total_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <ByUserPanel days={days} />
    </div>
  );
};

// =========================================================================
// BY-USER PANEL (PRP-010)
// =========================================================================

interface ByUserResp {
  users: Array<{
    user_id: string;
    email: string | null;
    name: string | null;
    total_cost_usd: number;
    request_count: number;
    last_request_at: string;
    daily_sparkline: number[];
  }>;
  window: { start: string; end: string; days: number };
  total_users: number;
  total_cost_usd: number;
}

const ByUserPanel: React.FC<{ days: number }> = ({ days }) => {
  const [data, setData] = useState<ByUserResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drillUserId, setDrillUserId] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    const end = new Date();
    const start = new Date(Date.now() - days * 86400_000);
    BackendAPI.adminUsageByUser({
      start: start.toISOString(),
      end: end.toISOString(),
      limit: 10,
    })
      .then(setData)
      .catch((err) => setError(err?.message || 'Failed to load per-user usage'));
  }, [days]);

  return (
    <>
      <Panel title={`Top users by spend (last ${days} days)`}>
        {error && (
          <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>
        )}
        {!error && !data && <div className="text-sm text-muted-foreground">Loading...</div>}
        {data && data.users.length === 0 && (
          <p className="text-sm text-muted-foreground">No attributed user activity in this window.</p>
        )}
        {data && data.users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="pb-2">User</th>
                  <th className="pb-2">Spend trend</th>
                  <th className="pb-2 text-right">Requests</th>
                  <th className="pb-2 text-right">Last seen</th>
                  <th className="pb-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="cursor-pointer border-t hover:bg-slate-50 dark:hover:bg-muted"
                    onClick={() => setDrillUserId(u.user_id)}
                  >
                    <td className="py-2">
                      <div className="font-medium">{u.name || u.email || u.user_id}</div>
                      {u.email && u.name && (
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      )}
                    </td>
                    <td className="py-2">
                      <Sparkline values={u.daily_sparkline} />
                    </td>
                    <td className="py-2 text-right">{u.request_count}</td>
                    <td className="py-2 text-right text-xs text-muted-foreground">
                      {u.last_request_at?.slice(0, 16).replace('T', ' ')}
                    </td>
                    <td className="py-2 text-right font-medium">{formatUsd(u.total_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <p className="mt-2 text-xs text-muted-foreground">
            {data.total_users} total users • {formatUsd(data.total_cost_usd)} attributed spend
          </p>
        )}
      </Panel>

      {drillUserId && (
        <UserDetailDrawer
          userId={drillUserId}
          onClose={() => setDrillUserId(null)}
          windowDays={days}
        />
      )}
    </>
  );
};

// -------------------------------------------------------------------------
// User detail drawer (modal-style) — drilled in from the by-user table
// -------------------------------------------------------------------------

const UserDetailDrawer: React.FC<{
  userId: string;
  onClose: () => void;
  windowDays: number;
}> = ({ userId, onClose, windowDays }) => {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof BackendAPI.adminUsageByUserDetail>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    const end = new Date();
    const start = new Date(Date.now() - windowDays * 86400_000);
    BackendAPI.adminUsageByUserDetail(userId, {
      start: start.toISOString(),
      end: end.toISOString(),
    })
      .then(setDetail)
      .catch((err) => setError(err?.message || 'Failed to load user detail'));
  }, [userId, windowDays]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-end bg-black/30"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-2xl overflow-y-auto bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {detail?.user?.name || detail?.user?.email || userId}
            </h2>
            {detail?.user?.email && (
              <p className="text-sm text-muted-foreground">{detail.user.email}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>
        )}
        {!error && !detail && <div className="text-sm text-muted-foreground">Loading...</div>}

        {detail && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <SummaryCard label={`Spend (${detail.window.days}d)`} value={formatUsd(detail.total_cost_usd)} />
              <SummaryCard label="Requests" value={String(detail.total_requests)} />
            </div>

            <Panel title="Daily spend">
              <StackedBarChart
                buckets={detail.by_day.map((d) => ({
                  label: d.date.slice(5),
                  segments: d.by_model,
                  total: d.total_usd,
                }))}
                ariaLabel="Per-user daily LLM cost stacked by model"
              />
            </Panel>

            <Panel title="By endpoint">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="pb-2">Endpoint</th>
                    <th className="pb-2 text-right">Requests</th>
                    <th className="pb-2 text-right">Avg</th>
                    <th className="pb-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.by_endpoint.length === 0 && (
                    <tr><td colSpan={4} className="py-2 text-center text-muted-foreground">No requests.</td></tr>
                  )}
                  {detail.by_endpoint.map((e) => (
                    <tr key={e.endpoint} className="border-t">
                      <td className="py-1.5 font-mono text-xs">{e.endpoint}</td>
                      <td className="py-1.5 text-right">{e.request_count}</td>
                      <td className="py-1.5 text-right">{formatUsd(e.avg_usd)}</td>
                      <td className="py-1.5 text-right font-medium">{formatUsd(e.total_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            <Panel title="By model">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="pb-2">Model</th>
                    <th className="pb-2 text-right">Input tok</th>
                    <th className="pb-2 text-right">Output tok</th>
                    <th className="pb-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.by_model.map((m) => (
                    <tr key={m.model} className="border-t">
                      <td className="py-1.5 font-mono text-xs">{m.model}</td>
                      <td className="py-1.5 text-right">{m.input_tokens.toLocaleString()}</td>
                      <td className="py-1.5 text-right">{m.output_tokens.toLocaleString()}</td>
                      <td className="py-1.5 text-right font-medium">{formatUsd(m.total_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            <Panel title="Recent calls (last 20)">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="pb-2">When</th>
                    <th className="pb-2">Endpoint</th>
                    <th className="pb-2">Model</th>
                    <th className="pb-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recent_calls.length === 0 && (
                    <tr><td colSpan={4} className="py-2 text-center text-muted-foreground">No calls.</td></tr>
                  )}
                  {detail.recent_calls.map((c, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-1 text-xs">{c.ts?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-1 font-mono text-xs">{c.endpoint}</td>
                      <td className="py-1 font-mono text-xs">{c.model}</td>
                      <td className="py-1 text-right">{formatUsd(c.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
};

// =========================================================================
// THRESHOLDS TAB (PRP-010)
// =========================================================================

interface ThresholdRow {
  id: string;
  scope: 'per_user' | 'global';
  user_id: string | null;
  user_email: string | null;
  metric: string;
  threshold_value: number;
  action: 'alert' | 'soft_block' | 'hard_block';
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

const METRIC_OPTIONS = [
  { value: 'daily_cost_usd', label: 'Daily cost ($)' },
  { value: 'hourly_cost_usd', label: 'Hourly cost ($)' },
  { value: 'requests_per_minute', label: 'Requests / min' },
];

const ACTION_OPTIONS: Array<{ value: ThresholdRow['action']; label: string; tone: string }> = [
  { value: 'alert', label: 'Alert only', tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  { value: 'soft_block', label: 'Soft block', tone: 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-500/15' },
  { value: 'hard_block', label: 'Hard block', tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
];

const ThresholdsTab: React.FC = () => {
  const [rows, setRows] = useState<ThresholdRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [blocked, setBlocked] = useState<Array<{ user_id: string; email: string | null; action: string; triggered_at: string; threshold_id: string }>>([]);

  const refresh = () => {
    setError(null);
    Promise.all([BackendAPI.adminListThresholds(), BackendAPI.adminBlockedUsers()])
      .then(([t, b]) => {
        setRows(t.thresholds);
        setBlocked(b.blocked);
      })
      .catch((err) => setError(err?.message || 'Failed to load thresholds'));
  };

  useEffect(refresh, []);

  const updateField = async (id: string, patch: Partial<ThresholdRow>) => {
    setBusy(true);
    try {
      await BackendAPI.adminUpdateThreshold(id, patch as any);
      refresh();
    } catch (err: any) {
      alert(`Update failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!confirm('Delete this threshold?')) return;
    setBusy(true);
    try {
      await BackendAPI.adminDeleteThreshold(id);
      refresh();
    } catch (err: any) {
      alert(`Delete failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const evaluateNow = async () => {
    setBusy(true);
    try {
      const res = await BackendAPI.adminEvaluateThresholds();
      alert(`Checked ${res.thresholds_checked} thresholds, generated ${res.alerts_created} new alert(s).`);
      refresh();
    } catch (err: any) {
      alert(`Evaluation failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (userId: string) => {
    if (!confirm('Clear all unacknowledged block alerts for this user?')) return;
    setBusy(true);
    try {
      const res = await BackendAPI.adminUnblockUser(userId);
      alert(`Cleared ${res.cleared_alerts} alert(s).`);
      refresh();
    } catch (err: any) {
      alert(`Unblock failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-4 text-sm text-red-800 dark:text-red-300">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Thresholds evaluate every 60 seconds in production. Use "Evaluate now" to test.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={evaluateNow}
            disabled={busy}
            className="rounded border px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-muted disabled:opacity-50"
          >
            Evaluate now
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            + Add threshold
          </button>
        </div>
      </div>

      {blocked.length > 0 && (
        <Panel title="Currently blocked users">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">User</th>
                <th className="pb-2">Action</th>
                <th className="pb-2">Since</th>
                <th className="pb-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {blocked.map((b) => (
                <tr key={`${b.user_id}-${b.threshold_id}`} className="border-t">
                  <td className="py-1.5">{b.email || b.user_id}</td>
                  <td className="py-1.5">
                    <span className={cn('rounded px-2 py-0.5 text-xs', b.action === 'hard_block' ? 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300' : 'bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300')}>
                      {b.action}
                    </span>
                  </td>
                  <td className="py-1.5 text-xs text-muted-foreground">
                    {b.triggered_at?.slice(0, 16).replace('T', ' ')}
                  </td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => unblock(b.user_id)}
                      disabled={busy}
                      className="rounded border border-green-300 dark:border-green-500/40 px-2 py-0.5 text-xs text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-500/15 disabled:opacity-50"
                    >
                      Unblock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title="Active thresholds">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">Scope</th>
                <th className="pb-2">Metric</th>
                <th className="pb-2 text-right">Threshold</th>
                <th className="pb-2">Action</th>
                <th className="pb-2 text-center">Enabled</th>
                <th className="pb-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="py-3 text-center text-muted-foreground">No thresholds configured.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2 text-xs">
                    {r.scope === 'global' ? (
                      <span className="rounded bg-blue-50 dark:bg-blue-500/15 px-2 py-0.5 text-blue-700 dark:text-blue-300">global</span>
                    ) : r.user_id ? (
                      <span title={r.user_id}>per user: {r.user_email || r.user_id.slice(0, 8)}</span>
                    ) : (
                      <span className="rounded bg-muted px-2 py-0.5">per-user default</span>
                    )}
                  </td>
                  <td className="py-2 font-mono text-xs">{r.metric}</td>
                  <td className="py-2 text-right">
                    <input
                      type="number"
                      step={0.01}
                      min={0}
                      value={r.threshold_value}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, threshold_value: v } : x));
                      }}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0) updateField(r.id, { threshold_value: v });
                      }}
                      className="w-24 rounded border px-2 py-0.5 text-right text-sm"
                    />
                  </td>
                  <td className="py-2">
                    <select
                      value={r.action}
                      onChange={(e) => updateField(r.id, { action: e.target.value as ThresholdRow['action'] })}
                      disabled={busy}
                      className={cn('rounded border px-2 py-0.5 text-xs', ACTION_OPTIONS.find((a) => a.value === r.action)?.tone)}
                    >
                      {ACTION_OPTIONS.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 text-center">
                    <input
                      type="checkbox"
                      checked={r.enabled}
                      onChange={(e) => updateField(r.id, { enabled: e.target.checked })}
                      disabled={busy}
                    />
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => deleteRow(r.id)}
                      disabled={busy}
                      className="text-xs text-red-700 dark:text-red-300 hover:underline disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {showAdd && (
        <AddThresholdModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </div>
  );
};

const AddThresholdModal: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [scope, setScope] = useState<'per_user' | 'global'>('per_user');
  const [userId, setUserId] = useState('');
  const [metric, setMetric] = useState('daily_cost_usd');
  const [value, setValue] = useState('5');
  const [action, setAction] = useState<ThresholdRow['action']>('alert');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const n = parseFloat(value);
    if (isNaN(n) || n <= 0) {
      setError('Threshold must be a positive number');
      return;
    }
    setBusy(true);
    try {
      await BackendAPI.adminCreateThreshold({
        scope,
        user_id: scope === 'per_user' && userId ? userId : null,
        metric,
        threshold_value: n,
        action,
        enabled: true,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to create threshold');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Add threshold</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-muted-foreground">Scope</label>
            <select value={scope} onChange={(e) => setScope(e.target.value as any)} className="mt-1 w-full rounded border px-2 py-1">
              <option value="per_user">Per user (default for all users)</option>
              <option value="global">Global</option>
            </select>
          </div>
          {scope === 'per_user' && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground">User ID (optional — leave blank for default)</label>
              <input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="user_xyz (overrides default for this user only)"
                className="mt-1 w-full rounded border px-2 py-1 font-mono text-xs"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-muted-foreground">Metric</label>
            <select value={metric} onChange={(e) => setMetric(e.target.value)} className="mt-1 w-full rounded border px-2 py-1">
              {METRIC_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground">Threshold value</label>
            <input
              type="number" min={0} step={0.01}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground">Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value as any)} className="mt-1 w-full rounded border px-2 py-1">
              {ACTION_OPTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          {error && <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-2 text-xs text-red-800 dark:text-red-300">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-xs">Cancel</button>
            <button type="button" onClick={submit} disabled={busy} className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50">
              {busy ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// USERS TAB
// =========================================================================

interface UserRow {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  created_at: string;
  roles: string[];
  project_count: number;
  spend_window_usd: number;
  spend_all_time_usd: number;
  credit_balance: number;
  credits_used: number;
  credits_purchased: number;
  amount_paid_usd: number;
  last_active: string | null;
  estimate_count: number;
  line_items_total: number;
  unmatched_total: number;
  error_count: number;
  health: 'ok' | 'look' | 'error' | 'none';
}

/** Maps the backend-computed `health` rollup to a colored status chip.
 *  Taxonomy is decided server-side (Task 4) — this just renders it. */
const healthChip = (user: UserRow): JSX.Element => {
  const styles: Record<UserRow['health'], string> = {
    error: 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300',
    look: 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    ok: 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300',
    none: 'bg-muted text-slate-400 dark:text-slate-500',
  };
  const labels: Record<UserRow['health'], string> = {
    error: '🔴 Error',
    look: '🟡 Look',
    ok: '🟢 Clean',
    none: '⚪ —',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs whitespace-nowrap', styles[user.health])}>
      {labels[user.health]}
    </span>
  );
};

/** Roster filter predicate: users whose health rollup flags something to look at. */
const needsAttention = (user: UserRow): boolean => user.health !== 'ok' && user.health !== 'none';

/**
 * At-a-glance credit cell for the roster: shows the current balance (+ lifetime
 * used) from the roster row — no per-row fetch — and reveals an inline grant
 * input on click. Granting mints a redeemable code emailed to the user (the
 * redeem-code path; they see the increment on redeem). B-318.
 */
const CreditsCell: React.FC<{ user: UserRow; onGranted: () => void }> = ({ user, onGranted }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<'direct' | 'code'>('direct');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const grant = async () => {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setMsg('Enter a positive number of credits.');
      return;
    }
    const how =
      mode === 'direct'
        ? `Add ${n} credit(s) to ${user.email} now? It lands on their balance immediately.`
        : `Grant ${n} credit(s) to ${user.email}? They'll get an email with a redeemable code.`;
    if (!confirm(how)) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await BackendAPI.adminAddUserCredits(user.id, n, mode);
      if (res.mode === 'direct') {
        setMsg(`Added — balance is now ${res.balance}.`);
      } else {
        setMsg(
          res.emailed
            ? `Emailed code ${res.code}.`
            : `Code ${res.code} (no email on file / email off; send it manually).`,
        );
      }
      setAmount('');
      onGranted();
    } catch (err: any) {
      setMsg(`Grant failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title="Click to grant credits"
        className="text-left hover:underline"
        data-testid="credit-balance"
      >
        <span className="font-medium">{user.credit_balance}</span>
        {user.credits_used > 0 && (
          <span className="ml-1 text-[11px] text-muted-foreground">
            ({user.credits_used} used)
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="+credits"
              aria-label={`Add credits for ${user.email}`}
              className="w-16 rounded border px-1 py-0.5 text-xs"
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => void grant()}
              disabled={busy}
              className="rounded border border-primary bg-primary px-2 py-0.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? '…' : 'Grant'}
            </button>
          </div>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'direct' | 'code')}
            aria-label="Grant method"
            className="w-full rounded border px-1 py-0.5 text-[11px]"
            disabled={busy}
          >
            <option value="direct">Add to balance now</option>
            <option value="code">Email a redeemable code</option>
          </select>
        </div>
      )}
      {msg && <div className="text-[11px] text-muted-foreground">{msg}</div>}
    </div>
  );
};

/**
 * Feedback inbox. Lists submitted feedback (default: the pending queue) and
 * lets the founder reward it (grant credits directly) or dismiss it in one
 * click. The reward lands on the submitter's balance immediately.
 */
const FeedbackTab: React.FC = () => {
  type Row = Awaited<ReturnType<typeof BackendAPI.adminListFeedback>>[number];
  const [rows, setRows] = useState<Row[]>([]);
  const [status, setStatus] = useState<string>('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    BackendAPI.adminListFeedback(status ? { status } : {})
      .then(setRows)
      .catch((e) => setError(e?.message || 'Failed to load feedback'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const reward = async (row: Row) => {
    const raw = window.prompt(`How many credits to grant ${row.email || row.user_id}?`, '1');
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) {
      alert('Enter a positive number of credits.');
      return;
    }
    setBusyId(row.id);
    try {
      const res = await BackendAPI.adminReviewFeedback(row.id, 'reward', n);
      alert(`Granted ${res.credits_granted} credit(s). New balance: ${res.balance ?? '—'}.`);
      load();
    } catch (e: any) {
      alert(`Reward failed: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (row: Row) => {
    if (!confirm('Dismiss this feedback with no reward?')) return;
    setBusyId(row.id);
    try {
      await BackendAPI.adminReviewFeedback(row.id, 'dismiss');
      load();
    } catch (e: any) {
      alert(`Dismiss failed: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-sm text-muted-foreground">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="pending">Pending</option>
          <option value="rewarded_manual">Rewarded</option>
          <option value="reviewed_no_reward">Dismissed</option>
          <option value="">All</option>
        </select>
        <button
          type="button"
          onClick={load}
          className="rounded border px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-muted"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}
      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded border bg-card p-6 text-center text-sm text-muted-foreground">
          No feedback {status ? `with status “${status}”` : ''} yet.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{row.email || row.user_id}</div>
                <div className="text-[11px] text-muted-foreground">
                  {row.created_at?.slice(0, 16).replace('T', ' ')}
                  {row.rating != null && ` · rated ${row.rating}/5`}
                  {row.would_pay && ` · would pay: ${row.would_pay}`}
                  {` · ${row.status}`}
                </div>
              </div>
              {row.status === 'pending' && (
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => void reward(row)}
                    disabled={busyId === row.id}
                    className="rounded border border-primary bg-primary px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                  >
                    Reward
                  </button>
                  <button
                    type="button"
                    onClick={() => void dismiss(row)}
                    disabled={busyId === row.id}
                    className="rounded border px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-muted disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
            {row.body && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{row.body}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Send a one-off email to a user from the account drawer. Surfaces clearly
 * when the email provider is off (nothing was delivered) so the founder isn't
 * misled into thinking a message went out.
 */
const EmailUserPanel: React.FC<{ userId: string; email: string | null }> = ({ userId, email }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      setMsg('Add a subject and a message.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await BackendAPI.adminEmailUser(userId, subject.trim(), body.trim());
      if (!res.email_enabled) {
        setMsg('Email is turned off (EMAIL_PROVIDER=none) — nothing was sent.');
      } else if (!res.to_email) {
        setMsg('No email address on file for this user.');
      } else if (res.emailed) {
        setMsg(`Sent to ${res.to_email}.`);
        setSubject('');
        setBody('');
      } else {
        setMsg('Send failed — check the email log.');
      }
    } catch (e: any) {
      setMsg(`Send failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Email this user">
      {email ? (
        <div className="space-y-2">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full rounded border px-2 py-1 text-xs"
            disabled={busy}
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Message to ${email}`}
            rows={4}
            className="w-full rounded border px-2 py-1 text-xs"
            disabled={busy}
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={busy}
            className="rounded border border-primary bg-primary px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send email'}
          </button>
          {msg && <div className="text-[11px] text-muted-foreground">{msg}</div>}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No email address on file.</p>
      )}
    </Panel>
  );
};

/**
 * User account card (drawer). Opened by clicking a roster row. Shows the
 * "rest of the info": email activation, how they joined, subscription + billing
 * period, recent credit ledger — plus role management and the destructive
 * actions (activate/deactivate, hard delete) that don't belong in the at-a-
 * glance row.
 */
export const UserActivityDrawer: React.FC<{
  user: UserRow;
  roleCatalog: Array<{ id: string; name: string; description: string }>;
  isSuperAdmin: boolean;
  onClose: () => void;
  onChanged: () => void;
}> = ({ user, roleCatalog, isSuperAdmin, onClose, onChanged }) => {
  const [account, setAccount] =
    useState<Awaited<ReturnType<typeof BackendAPI.adminUserAccount>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState<string[]>(user.roles);
  const [estimates, setEstimates] =
    useState<Awaited<ReturnType<typeof BackendAPI.adminUserEstimates>>['estimates'] | null>(null);
  const [estimatesError, setEstimatesError] = useState<string | null>(null);
  const [logins, setLogins] =
    useState<Awaited<ReturnType<typeof BackendAPI.adminUserLogins>> | null>(null);
  const [loginsError, setLoginsError] = useState<string | null>(null);
  const [expandedEstimate, setExpandedEstimate] = useState<string | null>(null);
  const [estimateDetails, setEstimateDetails] =
    useState<Record<string, Awaited<ReturnType<typeof BackendAPI.adminEstimateDetail>>>>({});
  const [estimateDetailLoading, setEstimateDetailLoading] = useState<Record<string, boolean>>({});
  const [estimateDetailError, setEstimateDetailError] = useState<Record<string, string>>({});

  useEffect(() => {
    setError(null);
    BackendAPI.adminUserAccount(user.id)
      .then(setAccount)
      .catch((e) => setError(e?.message || 'Failed to load account'));
  }, [user.id]);

  useEffect(() => {
    setEstimatesError(null);
    BackendAPI.adminUserEstimates(user.id)
      .then((res) => setEstimates(res.estimates))
      .catch((e) => setEstimatesError(e?.message || 'Failed to load estimates'));
  }, [user.id]);

  useEffect(() => {
    setLoginsError(null);
    BackendAPI.adminUserLogins(user.id)
      .then(setLogins)
      .catch((e) => setLoginsError(e?.message || 'Failed to load logins'));
  }, [user.id]);

  const toggleRole = async (roleId: string) => {
    const next = roles.includes(roleId)
      ? roles.filter((r) => r !== roleId)
      : [...roles, roleId];
    setBusy(true);
    try {
      await BackendAPI.adminSetUserRoles(user.id, next);
      setRoles(next);
      onChanged();
    } catch (err: any) {
      alert(`Role update failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (!isSuperAdmin) {
      alert('Only super admins can change a user’s status.');
      return;
    }
    setBusy(true);
    try {
      if (user.is_active) {
        if (!confirm(`Deactivate ${user.email}? They lose access until reactivated.`)) {
          setBusy(false);
          return;
        }
        await BackendAPI.adminDeactivateUser(user.id);
      } else {
        await BackendAPI.adminActivateUser(user.id);
      }
      onChanged();
      onClose();
    } catch (err: any) {
      alert(`Update failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!isSuperAdmin) {
      alert('Only super admins can delete users.');
      return;
    }
    if (
      !confirm(
        `Permanently DELETE ${user.email}?\n\nThis removes their account, projects, credits, and usage history. It cannot be undone.`,
      )
    )
      return;
    if (!confirm(`Final confirmation — delete ${user.email} forever?`)) return;
    setBusy(true);
    try {
      const res = await BackendAPI.adminDeleteUser(user.id);
      alert(`Deleted ${user.email} — ${res.projects_deleted} project(s) removed.`);
      onChanged();
      onClose();
    } catch (err: any) {
      alert(`Delete failed: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (s: string | null | undefined) =>
    s ? s.slice(0, 10) : '—';

  const toggleEstimate = (key: string, publicRef: string | null) => {
    setExpandedEstimate((cur) => (cur === key ? null : key));
    if (!publicRef) return; // import-job failure rows have no detail endpoint
    if (estimateDetails[key] || estimateDetailLoading[key]) return; // already fetched/fetching
    setEstimateDetailLoading((prev) => ({ ...prev, [key]: true }));
    setEstimateDetailError((prev) => {
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });
    BackendAPI.adminEstimateDetail(publicRef)
      .then((detail) => {
        setEstimateDetails((prev) => ({ ...prev, [key]: detail }));
      })
      .catch((e: any) => {
        setEstimateDetailError((prev) => ({ ...prev, [key]: e?.message || 'Failed to load estimate detail' }));
      })
      .finally(() => {
        setEstimateDetailLoading((prev) => ({ ...prev, [key]: false }));
      });
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-end bg-black/30"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-lg overflow-y-auto bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{user.name || user.email}</h2>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <SummaryCard label="Projects" value={String(user.project_count)} />
          <SummaryCard label="Credit balance" value={String(user.credit_balance)} />
        </div>

        <div className="mt-3 grid grid-cols-4 gap-2">
          <SummaryCard label="Estimates" value={String(user.estimate_count)} />
          <SummaryCard label="Line items" value={String(user.line_items_total)} />
          <SummaryCard label="Unmatched" value={String(user.unmatched_total)} />
          <SummaryCard label="Errors" value={String(user.error_count)} />
        </div>
        <div className="mt-2">{healthChip(user)}</div>

        <div className="mt-5 space-y-4 text-sm">
          <Panel title="Account">
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
              <dt className="text-muted-foreground">Email activated</dt>
              <dd>
                {account?.email_verified_at ? (
                  <span className="text-green-700 dark:text-green-300">Verified {fmtDate(account.email_verified_at)}</span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-300">Not verified</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Joined</dt>
              <dd>{fmtDate(account?.created_at || user.created_at)}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>{user.is_active ? 'Active' : 'Deactivated'}</dd>
              <dt className="text-muted-foreground">How they joined</dt>
              <dd className="truncate" title={account?.signup_user_agent || ''}>
                {account?.signup_user_agent
                  ? account.signup_user_agent.slice(0, 40)
                  : '—'}
              </dd>
              <dt className="text-muted-foreground">Signup IP</dt>
              <dd>{account?.signup_ip || '—'}</dd>
            </dl>
          </Panel>

          <Panel title="Sessions & access">
            {loginsError && (
              <p className="text-xs text-red-700 dark:text-red-300">{loginsError}</p>
            )}
            {!loginsError && !logins && (
              <p className="text-xs text-muted-foreground">Loading…</p>
            )}
            {logins && (
              <>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <dt className="text-muted-foreground">Logins</dt>
                  <dd>{logins.count}</dd>
                  <dt className="text-muted-foreground">Last login</dt>
                  <dd>{logins.last_login?.slice(0, 10) || '—'}</dd>
                </dl>
                {logins.count === 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    No logins recorded yet (tracking starts at first login after this ships).
                  </p>
                ) : (
                  <table className="mt-2 w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="font-medium">IP</th>
                        <th className="font-medium">Count</th>
                        <th className="font-medium">Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logins.ips.map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-1">{row.ip || '—'}</td>
                          <td className="py-1">{row.n}</td>
                          <td className="py-1 text-muted-foreground">
                            {row.last_seen?.slice(0, 10) || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </Panel>

          <Panel title="Feature grants">
            <FeatureGrantsPanel userId={user.id} />
          </Panel>

          <Panel title="Estimates">
            {estimatesError && (
              <p className="text-xs text-red-700 dark:text-red-300">{estimatesError}</p>
            )}
            {!estimatesError && !estimates && (
              <p className="text-xs text-muted-foreground">Loading…</p>
            )}
            {estimates && estimates.length === 0 && (
              <p className="text-xs text-muted-foreground">No estimates yet.</p>
            )}
            {estimates && estimates.length > 0 && (
              <div className="space-y-1">
                {estimates.map((est, i) => {
                  const key = est.public_ref || String(i);
                  const isOpen = expandedEstimate === key;
                  const detail = estimateDetails[key];
                  const detailLoading = estimateDetailLoading[key];
                  const detailError = estimateDetailError[key];
                  return (
                    <div key={key}>
                      <button
                        type="button"
                        onClick={() => toggleEstimate(key, est.public_ref)}
                        className="flex w-full flex-col gap-0.5 rounded border border-border px-2 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-muted"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-medium">
                            {est.public_ref || '—'}
                          </span>
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[10px]',
                              est.outcome !== 'failed'
                                ? 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300'
                                : 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300',
                            )}
                          >
                            {est.outcome
                              ? est.outcome.charAt(0).toUpperCase() + est.outcome.slice(1)
                              : '—'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-muted-foreground">
                          <span className="truncate">{est.filename || '—'}</span>
                          <span>{est.created_at?.slice(0, 10) || '—'}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-muted-foreground">
                          <span>{est.line_items} line items</span>
                          <span>{est.room_count} rooms</span>
                          <span>{est.matched} matched</span>
                          <span>{est.unmatched} unmatched</span>
                          {est.likely_scanned && (
                            <span className="text-amber-700 dark:text-amber-300">⚠ likely scanned</span>
                          )}
                        </div>
                      </button>

                      {isOpen && (
                        <div className="mt-1 space-y-2 rounded border border-border bg-slate-50/50 dark:bg-muted px-2 py-2">
                          {!est.public_ref && (
                            <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 px-2 py-1.5 text-xs text-red-800 dark:text-red-300">
                              <div className="font-medium">{est.error_class || est.outcome}</div>
                              <p className="mt-0.5 text-red-700 dark:text-red-300">
                                Import-job failure — no detail available (no estimate was ever created).
                              </p>
                            </div>
                          )}

                          {est.public_ref && detailLoading && (
                            <p className="text-xs text-muted-foreground">Loading…</p>
                          )}
                          {est.public_ref && detailError && (
                            <p className="text-xs text-red-700 dark:text-red-300">{detailError}</p>
                          )}

                          {est.public_ref && detail && (
                            <>
                              {(detail.error || est.outcome === 'failed') && (
                                <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 px-2 py-1.5 text-xs text-red-800 dark:text-red-300">
                                  <div className="font-medium">
                                    {detail.error?.class || est.error_class || 'Failed'}
                                  </div>
                                  {detail.error?.message && (
                                    <p className="mt-0.5 text-red-700 dark:text-red-300">{detail.error.message}</p>
                                  )}
                                </div>
                              )}

                              {(detail.diagnostics || est.likely_scanned) && (
                                <div className="flex flex-wrap gap-1">
                                  {est.likely_scanned && (
                                    <span className="rounded-full bg-amber-50 dark:bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                                      ⚠ likely scanned
                                    </span>
                                  )}
                                  {detail.diagnostics?.confidence != null && (
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                                      confidence {Math.round(detail.diagnostics.confidence * 100)}%
                                    </span>
                                  )}
                                  {detail.diagnostics?.pages_total != null && (
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                                      {detail.diagnostics.pages_with_line_items ?? 0}/{detail.diagnostics.pages_total} pages with line items
                                    </span>
                                  )}
                                  {!!detail.diagnostics?.pages_unprocessable && (
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                                      {detail.diagnostics.pages_unprocessable} unprocessable page(s)
                                    </span>
                                  )}
                                  {detail.diagnostics?.warnings?.map((w, wi) => (
                                    <span key={wi} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                                      {w}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {detail.unmatched.length === 0 ? (
                                <p className="text-xs text-muted-foreground">
                                  No unmatched line items — everything mapped.
                                </p>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-muted-foreground">
                                      <th className="pb-1 font-medium">Description</th>
                                      <th className="pb-1 font-medium">Room</th>
                                      <th className="pb-1 font-medium">Reason</th>
                                      <th className="pb-1 text-right font-medium">Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.unmatched.map((u, ui) => (
                                      <tr key={ui} className="border-t border-border">
                                        <td className="py-1 pr-2">{u.description || '—'}</td>
                                        <td className="py-1 pr-2">{u.room}</td>
                                        <td className="py-1 pr-2 text-muted-foreground">{u.reason}</td>
                                        <td className="py-1 text-right">
                                          {u.quantity != null ? `${u.quantity}${u.unit ? ` ${u.unit}` : ''}` : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>

          <Panel title="Subscription">
            {account?.subscription ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <dt className="text-muted-foreground">Plan</dt>
                <dd className="capitalize">{account.subscription.tier}</dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="capitalize">{account.subscription.status}</dd>
                <dt className="text-muted-foreground">Billing period</dt>
                <dd>
                  {fmtDate(account.subscription.current_period_start)} –{' '}
                  {fmtDate(account.subscription.current_period_end)}
                </dd>
              </dl>
            ) : (
              <p className="text-xs text-muted-foreground">Free — no subscription on file.</p>
            )}
          </Panel>

          <Panel title="Roles">
            <div className="flex flex-wrap gap-1">
              {roleCatalog.map((role) => {
                const checked = roles.includes(role.id);
                const privileged = role.id === 'admin' || role.id === 'super_admin';
                const disabled = (privileged && !isSuperAdmin) || busy;
                return (
                  <label
                    key={role.id}
                    title={role.description}
                    className={cn(
                      'flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                      checked
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-slate-300 dark:border-border text-muted-foreground',
                      disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-3 w-3"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleRole(role.id)}
                    />
                    {role.name}
                  </label>
                );
              })}
            </div>
          </Panel>

          {account && account.recent_ledger.length > 0 && (
            <Panel title="Recent credit activity">
              <table className="w-full text-xs">
                <tbody>
                  {account.recent_ledger.map((l, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-1 text-muted-foreground">
                        {l.created_at?.slice(0, 10)}
                      </td>
                      <td className="py-1">{l.reason}</td>
                      <td className={cn('py-1 text-right font-medium', l.delta < 0 ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300')}>
                        {l.delta > 0 ? `+${l.delta}` : l.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          <EmailUserPanel userId={user.id} email={user.email} />

          <div className="flex items-center justify-between border-t pt-4">
            <button
              type="button"
              onClick={toggleActive}
              disabled={!isSuperAdmin || busy}
              className={cn(
                'rounded border px-3 py-1.5 text-xs font-medium',
                user.is_active
                  ? 'border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15'
                  : 'border-green-200 dark:border-green-500/30 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-500/15',
                (!isSuperAdmin || busy) && 'cursor-not-allowed opacity-40',
              )}
            >
              {user.is_active ? 'Deactivate' : 'Activate'}
            </button>
            <button
              type="button"
              onClick={del}
              disabled={!isSuperAdmin || busy}
              className={cn(
                'rounded border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/15 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20',
                (!isSuperAdmin || busy) && 'cursor-not-allowed opacity-40',
              )}
            >
              Delete user…
            </button>
          </div>
          {!isSuperAdmin && (
            <p className="text-[11px] text-muted-foreground">
              Status changes and deletion require super admin.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const UsersTab: React.FC<{ isSuperAdmin: boolean }> = ({ isSuperAdmin }) => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roleCatalog, setRoleCatalog] = useState<Array<{ id: string; name: string; description: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cardUser, setCardUser] = useState<UserRow | null>(null);
  const [access, setAccess] = useState<Record<string, UserAccessSummary>>({});

  const refresh = () => {
    Promise.all([BackendAPI.adminListUsers({ days: 30 }), BackendAPI.adminListRoles()])
      .then(([u, r]) => {
        setUsers(u.users);
        setRoleCatalog(r.roles);
      })
      .catch((err) => setError(err?.message || 'Failed to load users'));
    // PRP-MCP-9 G7: plan, founder, grants, last chat. Optional: the roster never waits on it or fails for it.
    Promise.resolve().then(() => AdminUserAccessAPI.summary()).then((r) => setAccess(r.users || {})).catch(() => setAccess({}));
  };

  useEffect(refresh, []);

  // Keep an open account card pointed at the freshest row after a refresh.
  useEffect(() => {
    setCardUser((cur) => (cur ? users.find((u) => u.id === cur.id) ?? null : cur));
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (!showDeactivated && !u.is_active) return false;
      if (attentionOnly && !needsAttention(u)) return false;
      if (roleFilter !== 'all' && !u.roles.includes(roleFilter)) return false;
      if (q && !`${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, search, showDeactivated, attentionOnly, roleFilter]);

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allFilteredSelected) filtered.forEach((u) => n.delete(u.id));
      else filtered.forEach((u) => n.add(u.id));
      return n;
    });

  const bulkSetActive = async (activate: boolean) => {
    if (!isSuperAdmin) {
      alert('Only super admins can change user status.');
      return;
    }
    const ids = filtered.filter((u) => selected.has(u.id) && u.is_active !== activate).map((u) => u.id);
    if (ids.length === 0) return;
    if (!confirm(`${activate ? 'Activate' : 'Deactivate'} ${ids.length} user(s)?`)) return;
    setPending(true);
    try {
      for (const id of ids) {
        if (activate) await BackendAPI.adminActivateUser(id);
        else await BackendAPI.adminDeactivateUser(id);
      }
      setSelected(new Set());
      refresh();
    } catch (err: any) {
      alert(`Bulk update failed: ${err?.message || err}`);
    } finally {
      setPending(false);
    }
  };

  if (error) {
    return <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-4 text-sm text-red-800 dark:text-red-300">{error}</div>;
  }

  const selCount = filtered.filter((u) => selected.has(u.id)).length;

  return (
    <div className="space-y-3">
      {/* Toolbar: role filter, search, status visibility, bulk actions. */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="rounded border px-2 py-1 text-xs"
          aria-label="Filter by role"
        >
          <option value="all">All roles</option>
          {roleCatalog.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email"
          aria-label="Search users"
          className="rounded border px-2 py-1 text-xs"
        />
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showDeactivated}
            onChange={(e) => setShowDeactivated(e.target.checked)}
          />
          Show deactivated
        </label>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={attentionOnly}
            onChange={(e) => setAttentionOnly(e.target.checked)}
          />
          Needs attention only
        </label>
        <div className="flex-1" />
        {selCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selCount} selected</span>
            <button
              type="button"
              disabled={!isSuperAdmin || pending}
              onClick={() => bulkSetActive(true)}
              className="rounded border border-green-200 dark:border-green-500/30 px-2 py-1 text-xs text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-500/15 disabled:opacity-40"
            >
              Activate
            </button>
            <button
              type="button"
              disabled={!isSuperAdmin || pending}
              onClick={() => bulkSetActive(false)}
              className="rounded border border-red-200 dark:border-red-500/30 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-40"
            >
              Deactivate
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Click a name for the user page (access, grants, chat estimates); click the row for roles and delete. “API cost” is our
        LLM spend on that user (attributed going forward; some historical calls are
        unattributed). “Paid” is what they purchased.
      </p>

      <div className="overflow-x-auto rounded-lg border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-2 py-2">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2 text-right">Projects</th>
              <th className="px-3 py-2">Credits</th>
              <th className="px-3 py-2 text-right">API cost 30d</th>
              <th className="px-3 py-2 text-right">API cost all-time</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Last active</th>
              <th className="px-3 py-2 text-right">Estimates</th>
              <th className="px-3 py-2 text-right">Line items</th>
              <th className="px-3 py-2 text-right">Unmatched</th>
              <th className="px-3 py-2 text-right">Errors</th>
              <th className="px-3 py-2">Health</th>
              <AccessHeaderCells />
              <th className="px-3 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={14 + ACCESS_COLUMN_COUNT} className="py-4 text-center text-muted-foreground">
                  No users match.
                </td>
              </tr>
            )}
            {filtered.map((u) => (
              <tr
                key={u.id}
                onClick={() => setCardUser(u)}
                className={cn(
                  'cursor-pointer border-t align-top hover:bg-slate-50 dark:hover:bg-muted',
                  !u.is_active && 'opacity-50',
                )}
              >
                <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggleSelected(u.id)}
                    aria-label={`Select ${u.email}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <Link to={`/admin/users/${encodeURIComponent(u.id)}`} onClick={(e) => e.stopPropagation()} className="block font-medium hover:underline">
                    {u.name || u.email}
                  </Link>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="px-3 py-3 text-right">{u.project_count}</td>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <CreditsCell user={u} onGranted={refresh} />
                </td>
                <td className="px-3 py-3 text-right">{formatUsd(u.spend_window_usd)}</td>
                <td className="px-3 py-3 text-right font-medium">{formatUsd(u.spend_all_time_usd)}</td>
                <td className="px-3 py-3 text-right">
                  {u.amount_paid_usd > 0 ? formatUsd(u.amount_paid_usd) : '—'}
                </td>
                <td className="px-3 py-3 text-right">{u.last_active?.slice(0, 10) || '—'}</td>
                <td className="px-3 py-3 text-right">{u.estimate_count}</td>
                <td className="px-3 py-3 text-right">{u.line_items_total}</td>
                <td className="px-3 py-3 text-right">{u.unmatched_total || '—'}</td>
                <td className="px-3 py-3 text-right">{u.error_count || '—'}</td>
                <td className="px-3 py-3">{healthChip(u)}</td>
                <AccessCells access={access[u.id]} />
                <td className="px-3 py-3 text-right">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs',
                      u.is_active ? 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300' : 'bg-muted text-slate-500 dark:text-muted-foreground',
                    )}
                  >
                    {u.is_active ? 'Active' : 'Deactivated'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cardUser && (
        <UserActivityDrawer
          key={cardUser.id}
          user={cardUser}
          roleCatalog={roleCatalog}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setCardUser(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
};

// =========================================================================
// PRICING TAB
// =========================================================================

const PricingTab: React.FC = () => {
  const [endpoints, setEndpoints] = useState<ByEndpointResp | null>(null);
  const [pricing, setPricing] = useState<any>(null);
  const [estimatesPerMonth, setEstimatesPerMonth] = useState(100);
  const [primaryEndpoint, setPrimaryEndpoint] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([BackendAPI.adminUsageByEndpoint({ days: 30 }), BackendAPI.adminPricing()])
      .then(([e, p]) => {
        setEndpoints(e);
        setPricing(p);
        // Default to /completeness/analyze if present; else the highest-cost endpoint.
        const analyze = e.endpoints.find((x) => x.endpoint.includes('completeness/analyze'));
        setPrimaryEndpoint(analyze?.endpoint || e.endpoints[0]?.endpoint || '');
      })
      .catch((err) => setError(err?.message || 'Failed to load pricing data'));
  }, []);

  const avgPerEstimate = useMemo(() => {
    if (!endpoints || !primaryEndpoint) return 0;
    return endpoints.endpoints.find((x) => x.endpoint === primaryEndpoint)?.avg_usd ?? 0;
  }, [endpoints, primaryEndpoint]);

  if (error) {
    return <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-4 text-sm text-red-800 dark:text-red-300">{error}</div>;
  }
  if (!endpoints || !pricing) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  const monthly = avgPerEstimate * estimatesPerMonth;

  return (
    <div className="space-y-6">
      <Panel title="Pricing simulator">
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Endpoint</label>
              <select
                value={primaryEndpoint}
                onChange={(e) => setPrimaryEndpoint(e.target.value)}
                className="mt-1 w-full rounded border px-2 py-1 text-sm font-mono"
              >
                {endpoints.endpoints.map((e) => (
                  <option key={e.endpoint} value={e.endpoint}>
                    {e.endpoint}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Observed avg: {formatUsd(avgPerEstimate)} / request
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Requests / month</label>
              <input
                type="number"
                min={0}
                value={estimatesPerMonth}
                onChange={(e) => setEstimatesPerMonth(parseInt(e.target.value || '0', 10))}
                className="mt-1 w-full rounded border px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div className="rounded-lg bg-primary/5 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Projected monthly cost</p>
            <p className="text-2xl font-bold">{formatUsd(monthly)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {estimatesPerMonth} × {formatUsd(avgPerEstimate)} (observed avg over last 30 days)
            </p>
          </div>
        </div>
      </Panel>

      <Panel title="Per-model price reference ($ per 1M tokens)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">Provider</th>
                <th className="pb-2">Model</th>
                <th className="pb-2 text-right">Input</th>
                <th className="pb-2 text-right">Cached input</th>
                <th className="pb-2 text-right">Output</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(pricing.prices_per_mtok || {}).map(([provider, models]: any) =>
                Object.entries(models).map(([model, p]: any) => (
                  <tr key={`${provider}-${model}`} className="border-t">
                    <td className="py-1.5 capitalize">{provider}</td>
                    <td className="py-1.5 font-mono text-xs">{model}</td>
                    <td className="py-1.5 text-right">${p.input.toFixed(2)}</td>
                    <td className="py-1.5 text-right">{p.cached_input ? `$${p.cached_input.toFixed(3)}` : '—'}</td>
                    <td className="py-1.5 text-right">${p.output.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

// =========================================================================
// Shared components
// =========================================================================

const Panel: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  right,
  children,
}) => (
  <section className="rounded-lg border bg-card p-4 shadow-sm">
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-sm font-semibold">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

const SummaryCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg border bg-card p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-bold">{value}</p>
  </div>
);

// -------------------------------------------------------------------------
// StackedBarChart — raw SVG, no chart-lib dependency
// -------------------------------------------------------------------------
// Each bucket: { label, segments: {seriesName: value}, total }.
// Bars are stacked vertically; series share a stable color palette across
// the chart so the legend is meaningful.

interface ChartBucket {
  label: string;
  segments: Record<string, number>;
  total: number;
}

const COLOR_PALETTE = [
  '#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed',
  '#0891b2', '#ea580c', '#65a30d', '#db2777', '#475569',
];

const StackedBarChart: React.FC<{ buckets: ChartBucket[]; ariaLabel?: string }> = ({
  buckets,
  ariaLabel,
}) => {
  const maxTotal = Math.max(0.0001, ...buckets.map((b) => b.total));
  // Stable series ordering by total volume across the chart so the legend
  // matches what dominates the bars.
  const seriesTotals: Record<string, number> = {};
  buckets.forEach((b) => {
    Object.entries(b.segments).forEach(([k, v]) => {
      seriesTotals[k] = (seriesTotals[k] || 0) + v;
    });
  });
  const orderedSeries = Object.entries(seriesTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([k]) => k);
  const colorFor = (series: string) => {
    const idx = orderedSeries.indexOf(series);
    return COLOR_PALETTE[idx % COLOR_PALETTE.length];
  };

  const height = 200;
  const barGap = 2;
  const containerWidth = Math.max(buckets.length * 28, 400);
  const barWidth = (containerWidth - (buckets.length + 1) * barGap) / Math.max(1, buckets.length);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${containerWidth} ${height + 20}`}
          className="block min-w-full"
          role="img"
          aria-label={ariaLabel || 'Stacked bar chart'}
        >
          {buckets.map((b, i) => {
            const x = barGap + i * (barWidth + barGap);
            let y = height;
            // Render segments biggest-first inside the bar so colors stay stable.
            const segs = Object.entries(b.segments).sort(([, a], [, c]) => c - a);
            return (
              <g key={i}>
                {segs.map(([series, value]) => {
                  const h = (value / maxTotal) * height;
                  y -= h;
                  return (
                    <rect
                      key={series}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={h}
                      fill={colorFor(series)}
                    >
                      <title>{`${b.label} · ${series}: ${formatUsd(value)}`}</title>
                    </rect>
                  );
                })}
                {/* Tick label */}
                <text
                  x={x + barWidth / 2}
                  y={height + 14}
                  fontSize="9"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {b.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {orderedSeries.length === 0 && (
          <span className="text-muted-foreground">No data in this window.</span>
        )}
        {orderedSeries.map((s) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: colorFor(s) }}
            />
            <span className="font-mono">{s}</span>
            <span className="text-muted-foreground">({formatUsd(seriesTotals[s])})</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Format helper
// -------------------------------------------------------------------------
// Switches between $0.0001 / $0.123 / $1.23 / $1,234.56 depending on
// magnitude so a $0.05 dev day doesn't read as "$0.00".

function formatUsd(value: number): string {
  if (!isFinite(value)) return '—';
  if (value === 0) return '$0.00';
  const abs = Math.abs(value);
  if (abs < 0.01) return `$${value.toFixed(4)}`;
  if (abs < 1) return `$${value.toFixed(3)}`;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// =========================================================================
// PARTNERS TAB (B-608) — super-admin only. Issue/revoke partner API keys,
// configure webhooks, suspend/reactivate partners. The backend gates every
// endpoint with require_role("super_admin"); this tab is only rendered for
// super-admins as a convenience.
// =========================================================================

type PartnerRow = Awaited<ReturnType<typeof BackendAPI.adminListPartners>>['partners'][number];

/** One-time reveal of a full key or webhook secret. Once dismissed it's gone
 *  from state and unrecoverable — matching the server contract. */
const ShowOnceModal: React.FC<{ title: string; secret: string; onClose: () => void }> = ({
  title,
  secret,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable; the value is selectable in the field */
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
          </button>
        </div>
        <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
          Copy this now — you won’t be able to see it again. It is not stored anywhere retrievable.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <input
            readOnly
            value={secret}
            aria-label="secret value"
            onFocus={(e) => e.target.select()}
            className="flex-1 rounded border bg-slate-50 dark:bg-muted px-2 py-2 font-mono text-xs"
          />
          <button
            type="button"
            onClick={copy}
            className="flex items-center gap-1 rounded border px-3 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-muted"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export const PartnersTab: React.FC = () => {
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<{ title: string; secret: string } | null>(null);

  // New-partner form.
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const refresh = () => {
    BackendAPI.adminListPartners()
      .then((r) => setPartners(r.partners))
      .catch((e) => setError(e?.message || 'Failed to load partners'));
  };
  useEffect(refresh, []);

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const createPartner = () =>
    guard(async () => {
      if (!newName.trim() || !newEmail.trim()) {
        setError('Name and contact email are required.');
        return;
      }
      await BackendAPI.adminCreatePartner(newName.trim(), newEmail.trim());
      setNewName('');
      setNewEmail('');
      refresh();
    });

  const createKey = (p: PartnerRow, env: 'test' | 'live') =>
    guard(async () => {
      if (env === 'live' && !confirm(`Create a LIVE key for ${p.name}? It will authenticate real requests.`)) return;
      const res = await BackendAPI.adminCreatePartnerKey(p.id, env);
      setReveal({ title: `New ${env} key for ${p.name}`, secret: res.full_key });
      refresh();
    });

  const revokeKey = (prefix: string) =>
    guard(async () => {
      if (!confirm(`Revoke key ${prefix}…? This cannot be undone.`)) return;
      await BackendAPI.adminRevokePartnerKey(prefix);
      refresh();
    });

  const toggleStatus = (p: PartnerRow) =>
    guard(async () => {
      const next = p.status === 'active' ? 'suspended' : 'active';
      if (next === 'suspended' && !confirm(`Suspend ${p.name}? All their keys will stop working.`)) return;
      await BackendAPI.adminSetPartnerStatus(p.id, next);
      refresh();
    });

  const configureWebhook = (p: PartnerRow) =>
    guard(async () => {
      const url = window.prompt(
        `Webhook URL for ${p.name} (blank to clear):`,
        p.webhook_url || '',
      );
      if (url === null) return; // cancelled
      const trimmed = url.trim();
      if (!trimmed) {
        await BackendAPI.adminSetPartnerWebhook(p.id, null, false);
        refresh();
        return;
      }
      const gen = confirm('Generate a new signing secret? (OK = yes, shown once)');
      const res = await BackendAPI.adminSetPartnerWebhook(p.id, trimmed, gen);
      if (res.webhook_secret) {
        setReveal({ title: `Webhook signing secret for ${p.name}`, secret: res.webhook_secret });
      }
      refresh();
    });

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>
      )}

      {/* New partner */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <div>
          <label className="block text-xs text-muted-foreground">Partner name</label>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Acme Roofing"
            className="rounded border px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground">Contact email</label>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="dev@acme.io"
            className="rounded border px-2 py-1 text-sm"
          />
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={createPartner}
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40"
        >
          New partner
        </button>
      </div>

      {partners.length === 0 && (
        <p className="text-sm text-muted-foreground">No partners yet.</p>
      )}

      {partners.map((p) => (
        <div key={p.id} className="rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{p.name}</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs',
                    p.status === 'active' ? 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300' : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300',
                  )}
                >
                  {p.status}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{p.contact_email}</div>
              <div className="text-[11px] text-muted-foreground">
                {p.webhook_url ? `Webhook: ${p.webhook_url}${p.has_webhook_secret ? ' 🔑' : ''}` : 'No webhook'}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => createKey(p, 'test')}
                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-muted disabled:opacity-40"
              >
                Create test key
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => createKey(p, 'live')}
                className="rounded border border-indigo-200 dark:border-indigo-500/30 px-2 py-1 text-xs text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 disabled:opacity-40"
              >
                Create live key
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => configureWebhook(p)}
                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-muted disabled:opacity-40"
              >
                Webhook…
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => toggleStatus(p)}
                className={cn(
                  'rounded border px-2 py-1 text-xs disabled:opacity-40',
                  p.status === 'active'
                    ? 'border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/15'
                    : 'border-green-200 dark:border-green-500/30 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-500/15',
                )}
              >
                {p.status === 'active' ? 'Suspend' : 'Reactivate'}
              </button>
            </div>
          </div>

          {/* Keys */}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-1">Prefix</th>
                  <th className="px-2 py-1">Env</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Last used</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {p.keys.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-2 text-muted-foreground">No keys.</td>
                  </tr>
                )}
                {p.keys.map((k) => (
                  <tr key={k.key_prefix} className="border-t">
                    <td className="px-2 py-1 font-mono text-xs">{k.key_prefix}…</td>
                    <td className="px-2 py-1">{k.environment}</td>
                    <td className="px-2 py-1">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs',
                          k.status === 'active' ? 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300' : 'bg-muted text-slate-500 dark:text-muted-foreground',
                        )}
                      >
                        {k.status}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-xs text-muted-foreground">
                      {k.last_used_at?.slice(0, 10) || 'never'}
                    </td>
                    <td className="px-2 py-1 text-right">
                      {k.status === 'active' && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => revokeKey(k.key_prefix)}
                          className="rounded border border-red-200 dark:border-red-500/30 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-40"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {reveal && (
        <ShowOnceModal title={reveal.title} secret={reveal.secret} onClose={() => setReveal(null)} />
      )}
    </div>
  );
};
